import {
  HttpClient,
  HttpContext,
  HttpEvent,
  HttpHeaders,
  HttpResponse,
  HttpXhrBackend,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ApiServiceMetaData } from '@shared/decorators';
import { ForceAny } from '@shared/types';
import { Observable, catchError, map, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LOADER_KEY } from '../utils/loader-context.token';
import { TOKENS } from '../../core/types';

@Injectable()
export class ApiService implements ApiServiceMetaData {
  /**
   * @override change base prefix according to apis required
   */
  prefix = 'api';

  /**
   * @override change base version according to apis required
   */
  version: `v${number}` = 'v2';

  /**
   * @override change base segment according to apis required
   */
  baseSegment = '';
  accessToken = localStorage.getItem(TOKENS.accessToken);

  /**
   * @override change httpOptions according to apis required
   */
  httpOptions = {
    headers: new HttpHeaders({}),
    ...(this.accessToken ? {} : { withCredentials: true }),
  };

  http = inject(HttpClient);
  /**
   * XHR is the older mechanism for performing HTTP requests in web browsers.
   * It’s still fully supported and widely used,
   * but the Fetch API offers a more modern, streamlined way of handling requests.
   * We have used Fetch Api for every api's call except the api calls where
   * we need upload progress (postUpdateProgress)
   */
  xhrHttpClient = inject(HttpClient);

  baseUrl = environment.base_url; // TODO: Inject this from environment

  /**
   * Get Base Url
   * @param uri string
   */
  getBaseUrl(ignoreBaseSegment = false, version?: string): string {
    return `${this.baseUrl}/${this.prefix}/${version ? version : this.version}${this.baseSegment && !ignoreBaseSegment ? `/${this.baseSegment}` : ''}`;
  }

  /**
   * Makes a POST request to the API.
   *
   * @param apiRoute The API route to make the request to.
   * @param body The data to send in the request body.
   * @returns An Observable of the typed response object.
   */
  post<TData, TRes extends object = ForceAny>(
    apiRoute: string,
    body: TData,
    customHeaders?: HttpHeaders | null,
    ignoreBaseSegment = false,
    header: Record<string, string> = {},
    contextKey?: string,
  ): Observable<TRes> {
    this.setHeaders(header);
    const headers = customHeaders ? customHeaders : this.httpOptions.headers;

    return this.http.post<TRes>(
      `${this.getBaseUrl(ignoreBaseSegment)}${apiRoute}`,
      body,
      {
        headers,
        ...(this.accessToken ? {} : { withCredentials: true }),
        ...(contextKey && {
          context: new HttpContext().set(LOADER_KEY, contextKey),
        }),
      },
    );
  }

  /**
   * Makes a POST request to the API and tracks the progress of the request.
   *
   * This method is intended for use cases where tracking the upload and/or download progress is needed.
   * It uses the `observe: 'events'` option to emit `HttpEvent` objects that provide information about
   * the request's progress, such as upload progress, download progress, and the final response.
   *
   * @param apiRoute The API route to make the request to.
   * @param body The data to send in the request body.
   * @param customHeaders Optional. Custom headers to include in the request.
   * @param accessToken Provide access-token explicitly as this is not preferred for use
   * and this call donot get accesstoken through interceptor (refer main-client-info.service for refer on how to pass access-token)
   * @returns An Observable that emits `HttpEvent` objects, including progress and the final response.
   */
  postUpdateProgress<TData, TRes extends object = ForceAny>(
    apiRoute: string,
    body: TData,
    customHeaders?: HttpHeaders,
    customUrl?: string,
  ): Observable<HttpEvent<TRes>> {
    // HttpEvent to capture events
    // Create a second HttpClient using HttpXhrBackend (XHR) specifically for uploads
    this.xhrHttpClient = new HttpClient(
      new HttpXhrBackend({ build: () => new XMLHttpRequest() }),
    );
    const headers = customHeaders ? customHeaders : this.httpOptions.headers;

    return this.xhrHttpClient.post<TRes>(
      `${customUrl ? customUrl : this.getBaseUrl()}${apiRoute}`,
      body,
      {
        headers,
        observe: 'events', // To get upload/download progress events
        reportProgress: true, // Enables the progress report
        ...(!customUrl && !this.accessToken ? { withCredentials: true } : {}),
      },
    );
  }

  /**
   * Makes a PUT request to the API and tracks the progress of the request.
   *
   * This method is intended for use cases where tracking the upload and/or download progress is needed.
   * It uses the `observe: 'events'` option to emit `HttpEvent` objects that provide information about
   * the request's progress, such as upload progress, download progress, and the final response.
   *
   * @param apiRoute The API route to make the request to.
   * @param body The data to send in the request body.
   * @param customHeaders Optional. Custom headers to include in the request.
   * @param accessToken Provide access-token explicitly as this is not preferred for use
   * and this call donot get accesstoken through interceptor (refer main-client-info.service for refer on how to pass access-token)
   * @returns An Observable that emits `HttpEvent` objects, including progress and the final response.
   */
  putUpdateProgress<TData, TRes extends object = ForceAny>(
    apiRoute: string,
    body: TData,
    customHeaders?: HttpHeaders,
    customUrl?: string,
    context?: string,
  ): Observable<HttpEvent<TRes>> {
    console.log('context', context);

    // HttpEvent to capture events
    // Create a second HttpClient using HttpXhrBackend (XHR) specifically for uploads
    this.xhrHttpClient = new HttpClient(
      new HttpXhrBackend({ build: () => new XMLHttpRequest() }),
    );
    const headers = customHeaders ? customHeaders : this.httpOptions.headers;

    return this.xhrHttpClient.put<TRes>(
      `${customUrl ? customUrl : this.getBaseUrl()}${apiRoute}`,
      body,
      {
        headers,
        ...(context && {
          context: new HttpContext().set(LOADER_KEY, context),
        }),
        observe: 'events', // To get upload/download progress events
        reportProgress: true, // Enables the progress report
        ...(!customUrl && !this.accessToken ? { withCredentials: true } : {}),
      },
    );
  }

  /**
   * Makes a POST request to the API.
   *
   * @param apiRoute The API route to make the request to.
   * @param body The data to send in the request body.
   * @param customHeaders Optional custom headers to send with the request.
   * @returns An Observable of the full HttpResponse object to access headers.
   */
  headersPostRequest<TData, TRes extends object = ForceAny>(
    apiRoute: string,
    body: TData,
    customHeaders?: HttpHeaders,
  ): Observable<HttpResponse<TRes>> {
    const headers = customHeaders ? customHeaders : this.httpOptions.headers;

    return this.http.post<HttpResponse<TRes>>(
      `${this.getBaseUrl()}${apiRoute}`,
      body,
      {
        headers,
        observe: 'response' as 'body', // This ensures that the full response is returned
        ...(this.accessToken ? {} : { withCredentials: true }),
      },
    );
  }

  /**
   * Makes a GET request to the API.
   *
   * @param apiRoute The API route to make the request to.
   * @returns An Observable of the full HttpResponse object to access headers.
   */
  headersGetRequest<TRes extends object>(
    apiRoute: string,
    version?: string,
  ): Observable<HttpResponse<TRes>> {
    const headers = this.httpOptions.headers;

    return this.http
      .get<HttpResponse<TRes>>(
        `${this.getBaseUrl(false, version)}${apiRoute}`,
        {
          headers,
          observe: 'response' as 'body', // This ensures that the full response is returned
          ...(this.accessToken ? {} : { withCredentials: true }),
        },
      )
      .pipe(catchError((error) => throwError(() => error)));
  }

  /**
   * Makes a GET request to the API.
   *
   * @param apiRoute The API route to make the request to.
   * @returns An Observable of the typed response object.
   */
  get<TRes extends object>(
    apiRoute: string,
    ignoreBaseSegment = false,
    header: Record<string, string> = {},
    version?: string,
    contextKey?: string,
  ): Observable<TRes> {
    this.setHeaders(header);
    const headers = this.httpOptions.headers;

    return this.http
      .get<TRes>(`${this.getBaseUrl(ignoreBaseSegment, version)}${apiRoute}`, {
        headers,
        ...(this.accessToken ? {} : { withCredentials: true }),
        ...(contextKey && {
          context: new HttpContext().set(LOADER_KEY, contextKey),
        }),
      })
      .pipe(catchError((error) => throwError(() => error)));
  }

  /**
   * Makes a PUT request to the API.
   *
   * @param apiRoute The API route to make the request to.
   * @param body The data to send in the request body.
   * @returns An Observable of the typed response object (usually void).
   */
  put<TData, TRes extends object = ForceAny>(
    apiRoute: string,
    body: TData,
    header = {},
    contextKey?: string,
  ): Observable<TRes> {
    this.setHeaders(header);
    const headers = this.httpOptions.headers;

    return this.http
      .put<TRes>(`${this.getBaseUrl()}${apiRoute}`, body, {
        headers,
        ...(contextKey && {
          context: new HttpContext().set(LOADER_KEY, contextKey),
        }),
        ...(this.accessToken ? {} : { withCredentials: true }),
      })
      .pipe(catchError((error) => throwError(() => error)));
  }

  /**
   * Makes a Patch request to the API.
   *
   * @param apiRoute The API route to make the request to.
   * @param body The data to send in the request body.
   * @returns An Observable of the typed response object (usually void).
   */
  patch<TData, TRes extends object = ForceAny>(
    apiRoute: string,
    body: TData,
    contextKey?: string,
    ignoreBaseSegment?: boolean,
  ): Observable<TRes> {
    const headers = this.httpOptions.headers;

    return this.http
      .patch<TRes>(`${this.getBaseUrl(ignoreBaseSegment)}${apiRoute}`, body, {
        headers,
        ...(contextKey && {
          context: new HttpContext().set(LOADER_KEY, contextKey),
        }),
        ...(this.accessToken ? {} : { withCredentials: true }),
      })
      .pipe(catchError((error) => throwError(() => error)));
  }

  /**
   * Makes a DELETE request to the API.
   *
   * @param apiRoute The API route to make the request to.
   * @returns An Observable representing the deletion operation.
   */
  delete<TRes extends object = ForceAny>(
    apiRoute: string,
    body?: ForceAny,
  ): Observable<TRes> {
    const headers = this.httpOptions.headers;

    return this.http
      .delete<TRes>(`${this.getBaseUrl()}${apiRoute}`, {
        headers,
        body,
        ...(this.accessToken ? {} : { withCredentials: true }),
      })
      .pipe(catchError((error) => throwError(() => error)));
  }

  /**
   * Makes a GET request to the API.
   *
   * @param apiRoute The API route to make the request to.
   * @returns An Observable of the typed response object.
   */
  multiTypeRequest<TRes extends object>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    apiRoute: string,
    body: Record<string, unknown> = {},
    ignoreBaseSegment = false,
    header: Record<string, string> = {},
  ): Observable<TRes> {
    this.setHeaders(header);
    const headers = this.httpOptions.headers;

    return this.http
      .request<TRes>(
        method,
        `${this.getBaseUrl(ignoreBaseSegment)}${apiRoute}`,
        {
          headers,
          body: method === 'POST' ? body : null,
          ...(this.accessToken ? {} : { withCredentials: true }),
        },
      )
      .pipe(catchError((error) => throwError(() => error)));
  }

  handleNestedObjectValues<T extends string | number | boolean | undefined>(
    key: string,
    obj: Record<string, unknown>,
    queryObj: Record<string, T[]>,
  ): void {
    for (const nestedKey in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, nestedKey)) continue;

      const compositeKey = `${key}[${nestedKey}]`;

      const nestedVal = obj[nestedKey];

      if (
        typeof nestedVal === 'string' ||
        typeof nestedVal === 'number' ||
        typeof nestedVal === 'boolean'
      ) {
        queryObj[compositeKey] = queryObj[compositeKey] ?? [];
        queryObj[compositeKey].push(nestedVal as T);
      } else if (Array.isArray(nestedVal)) {
        queryObj[compositeKey] = queryObj[compositeKey] ?? [];
        queryObj[compositeKey].push(nestedVal.join(',') as T);
      }
    }
  }

  /**
   * Converts an array of query parameter objects into a URL query string.
   *
   * @param queries An array of objects containing key-value pairs.
   * @returns A formatted URL query string, prefixed with a question mark (`?`).
   *          If the input array is empty, an empty string is returned.
   *
   * @throws {TypeError} If a value in the input objects is not a string, number, or boolean.
   */
  makeQueryParams<
    T extends string | number | boolean | undefined = string,
    DType extends object = object,
  >(queries: (DType extends object ? DType : Record<string, T>)[]): string {
    if (!queries) return '';

    const resQueries = Array.isArray(queries) ? queries : [queries];
    if (resQueries.length === 0) return '';

    const queryObj: Record<string, T[]> = {};

    resQueries.forEach((query) => {
      for (const key in query) {
        if (Object.prototype.hasOwnProperty.call(query, key)) {
          const currValue = query[key];

          if (
            typeof currValue === 'boolean' ||
            typeof currValue === 'string' ||
            typeof currValue === 'number'
          ) {
            queryObj[key] = queryObj[key] ?? [];
            queryObj[key].push(currValue as T);
          } else if (Array.isArray(currValue)) {
            queryObj[key] = queryObj[key] ?? [];
            queryObj[key].push(currValue.join(',') as T);
          }
          // ✅ Handle object with keys like tagNames: { COL_1: 'sad' }
          else if (typeof currValue === 'object' && currValue !== null) {
            this.handleNestedObjectValues<T>(
              key,
              currValue as Record<string, unknown>,
              queryObj,
            );
          }
        }
      }
    });

    const queryStr = Object.entries(queryObj)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value.join(','))}`,
      )
      .join('&');

    return queryStr.length ? `?${queryStr}` : '';
  }

  /**
   * Makes other domain request to the API.
   *
   * @param url The API route to make the request to.
   * @returns An Observable of the typed response object.
   */
  otherDomainApiCall<TRes extends object, TData = ForceAny>(
    method: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE',
    url: string,
    body?: TData,
  ): Observable<TRes> {
    const headers = this.httpOptions.headers;

    return this.http
      .request<TRes>(method, `${url}`, { body, headers })
      .pipe(catchError((error) => throwError(() => error)));
  }

  exportData(apiRoute: string, customUrl?: string): Observable<Blob> {
    const apiUrl = customUrl ?? `${this.getBaseUrl()}${apiRoute}`;
    const headers = this.httpOptions.headers;
    return this.http
      .request('GET', apiUrl, {
        headers,
        responseType: 'blob',
        ...(this.accessToken ? {} : { withCredentials: true }),
      })
      .pipe(catchError((error) => throwError(() => error)));
  }

  exportPostData(apiRoute: string, body: ForceAny): Observable<Blob> {
    const headers = this.httpOptions.headers;
    return this.http
      .request('POST', `${this.getBaseUrl()}${apiRoute}`, {
        body,
        headers,
        ...(this.accessToken ? {} : { withCredentials: true }),
        responseType: 'blob',
      })
      .pipe(catchError((error) => throwError(() => error)));
  }

  exportPutData(apiRoute: string, body: ForceAny): Observable<Blob> {
    const headers = this.httpOptions.headers;
    return this.http
      .request('PUT', `${this.getBaseUrl()}${apiRoute}`, {
        body,
        headers,
        ...(this.accessToken ? {} : { withCredentials: true }),
        responseType: 'blob',
      })
      .pipe(catchError((error) => throwError(() => error)));
  }

  exportPostDataV2(
    apiRoute: string,
    body: ForceAny,
    header = {},
  ): Observable<{ blob: Blob; fileName: string | null }> {
    this.setHeaders(header);
    const headers = this.httpOptions.headers;
    return this.http
      .request('POST', `${this.getBaseUrl()}${apiRoute}`, {
        body,
        headers,
        responseType: 'blob',
        observe: 'response',
        ...(this.accessToken ? {} : { withCredentials: true }),
      })
      .pipe(
        map((response: HttpResponse<Blob>) => {
          const contentDisposition = response.headers.get(
            'Content-Disposition',
          );
          const fileName =
            this.getFileNameFromContentDisposition(contentDisposition);
          return { blob: response.body as Blob, fileName };
        }),
        catchError((error) => throwError(() => error)),
      );
  }

  initHeaders(clientId: string) {
    this.setHeaders({ 'user-info': JSON.stringify({ clientId }) });
  }

  protected setHeaders(headers: Record<string, string>) {
    let updatedHeaders = this.httpOptions.headers;

    Object.entries(headers ?? {}).forEach(([key, value]) => {
      // every method (set,delete) will return new instance of HttpHeaders
      updatedHeaders = updatedHeaders.set(key, value);
    });

    this.httpOptions.headers = updatedHeaders;
  }

  private getFileNameFromContentDisposition(
    contentDisposition: string | null,
  ): string | null {
    if (!contentDisposition) return null;
    const matches = /filename="([^"]+)"/.exec(contentDisposition);
    return matches ? matches[1] : null;
  }
}
